import React, { useState } from 'react';
import { motion } from 'motion/react';
import { User, Mail, Building2, MessageSquare, Send, CheckCircle2, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { db } from '@/src/lib/firebase';
import { collection, addDoc, serverTimestamp, updateDoc, doc } from 'firebase/firestore';
import { handleFirestoreError, OperationType } from '@/src/lib/firestore-errors';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    interest: 'Cybersecurity Audit',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Single Production API Call: Handles storage, AI, and initial notification
      const response = await fetch('/api/automate-lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          type: 'general_contact',
          source: window.location.pathname
        })
      });

      if (!response.ok) throw new Error('Automation pipeline failed');
      
      setIsSuccess(true);
    } catch (error) {
      console.error("Submission failed:", error);
      alert("Something went wrong. Please try again or contact us directly at contact@agrandintl.com");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-12 space-y-6"
      >
        <div className="w-16 h-16 rounded-full bg-accent flex items-center justify-center text-background mx-auto">
          <CheckCircle2 className="w-8 h-8" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-display font-bold text-foreground">Message Sent</h3>
          <p className="text-muted-foreground">Thank you for reaching out. Fabiyan or a senior consultant will respond within 24 hours.</p>
        </div>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground ml-1">Full Name</label>
          <div className="relative">
            <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              required
              placeholder="Your Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="pl-10 bg-white/5 border-white/10 h-14 rounded-xl focus:border-accent/50 text-[13px] font-medium"
            />
          </div>
        </div>
        <div className="space-y-2">
          <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground ml-1">Business Email</label>
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              required
              type="email"
              placeholder="your@email.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="pl-10 bg-white/5 border-white/10 h-14 rounded-xl focus:border-accent/50 text-[13px] font-medium"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground ml-1">Phone (Optional)</label>
          <div className="relative">
            <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="tel"
              placeholder="+1 (555) 000-0000"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="pl-10 bg-white/5 border-white/10 h-14 rounded-xl focus:border-accent/50 text-[13px] font-medium"
            />
          </div>
        </div>
        <div className="space-y-2">
          <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground ml-1">Organization</label>
          <div className="relative">
            <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              required
              placeholder="Your Company"
              value={formData.company}
              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              className="pl-10 bg-white/5 border-white/10 h-14 rounded-xl focus:border-accent/50 text-[13px] font-medium"
            />
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground ml-1">Service Interest</label>
        <select 
          required
          value={formData.interest}
          onChange={(e) => setFormData({ ...formData, interest: e.target.value })}
          className="w-full bg-white/5 border border-white/10 h-14 rounded-xl focus:border-accent/50 px-4 text-[13px] font-medium text-foreground transition-all duration-300 outline-none appearance-none cursor-pointer"
        >
          <option value="Cybersecurity Audit" className="bg-background text-foreground">Cybersecurity Risk Audit — $299</option>
          <option value="Penetration Testing" className="bg-background text-foreground">Advanced Penetration Testing</option>
          <option value="Cloud Security" className="bg-background text-foreground">Cloud Hardening (AWS/Azure/GCP)</option>
          <option value="Compliance Support" className="bg-background text-foreground">Compliance Support (PCI/SOC2)</option>
          <option value="Complytics Demo" className="bg-background text-foreground">Complytics AI Platform Demo</option>
        </select>
      </div>

      <div className="space-y-2">
        <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground ml-1">Project Details</label>
        <div className="relative">
          <MessageSquare className="absolute left-4 top-5 w-4 h-4 text-muted-foreground" />
          <Textarea
            required
            placeholder="Tell us about your security challenges or current infrastructure..."
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            className="pl-10 pt-5 bg-white/5 border-white/10 min-h-[160px] rounded-xl focus:border-accent/50 resize-none text-[13px] font-medium"
          />
        </div>
      </div>
      <div className="p-5 rounded-2xl bg-accent/[0.03] border border-accent/10 space-y-2">
        <p className="text-[10px] font-bold uppercase tracking-widest text-accent">What happens next?</p>
        <p className="text-xs text-muted-foreground leading-relaxed">
          We'll review your details and Fabiyan or a senior consultant will reach out within 24 hours to schedule a short consultation. No pressure, just a practical discussion about your security roadmap.
        </p>
      </div>

      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full h-14 rounded-xl bg-accent hover:bg-accent/90 text-background font-bold text-base transition-all"
      >
        {isSubmitting ? "Sending..." : "Send Message"}
        <Send className="ml-2 w-4 h-4" />
      </Button>
      <p className="text-center text-[10px] text-muted-foreground/60 mt-4 uppercase tracking-widest font-bold">
        Your information is handled securely and confidentially.
      </p>
    </form>
  );
}
